import create from "zustand";
import { trackClearECommerce, trackEvent, trackFBEvent, trackPosthog } from "../../../helper/tracking/tracking";

declare var window: any;

type stepType = "login"|"register"|"choose"|"address"|"choose-payment"|"payment"|"success";

interface PhysicalProduct {
    id: number,
    title: string,
    price: number,
    priceInstead: number,
    sale: boolean,
    description: string
}

interface PaymentPhysicalProductState {

    showProductDialog: boolean;
    setShowProductDialog: (showProductDialog) => void;

    step: stepType;
    setStep: (step: stepType) => void;

    addressData: any;
    setAddressData: (data: any) => void;

    paymentMethod: string;
    setPaymentMethod: (paymentMethod: string) => void;

    hasAddressError: boolean;
    setHasAddressError: (hasAddressError) => void;

    errorMessage: string;
    setErrorMessage: (errorMessage: string) => void;

    amount: number;
    setAmount: (amount) => void;

    products: any[];
    setProducts: (products) => void;

    currentProduct: PhysicalProduct;
    setCurrentProduct: (product: PhysicalProduct) => void;

    postRequestData: any;
    update: () => void;

    totalPrice: number,
    totalPriceInstead: number,

    reset: () => void,

    trackedSteps: string[],
    trackPayment: (transactionId: string, paymentMethod: string) => void;

}

const trackStepIfNotTracked = (step, trackSteps, params = undefined) => {
    if (trackSteps?.indexOf(step) === -1) {
        trackPosthog("payment physical-product modal " + step, params);
    }
}

export const usePaymentPhysicalProductState = create<PaymentPhysicalProductState>(
    (set, get) => ({

        trackedSteps: [],

        reset: () => {
          set(
              {
                  step: "login",
                  totalPrice: 0,
                  totalPriceInstead: 0,
                  currentProduct: null,
                  trackedSteps: [],
                  errorMessage: null
              }
          );
        },

        totalPrice: 0,
        totalPriceInstead: 0,

        update: () => {
            set(
                {
                    postRequestData: {
                        amount: get().amount,
                        id: get().currentProduct?.id,
                        ...get().addressData
                    },
                    totalPrice: get().amount * get().currentProduct?.price,
                    totalPriceInstead: get().amount * get().currentProduct?.priceInstead
                }
            )
        },

        amount: 1,
        setAmount: (amount) => {
            set({amount});
            get().update();
        },

        showProductDialog: false,
        setShowProductDialog: (showProductDialog) => {
            set( { showProductDialog } );

            if (showProductDialog) {
                trackPosthog("start physical product buy modal");

                // calling internal setStep again to force tracking
                get().setStep( get().step );
            }
        } ,

        step: "register",
        setStep: (step) => {
            set( { step } );

            // only track if dialog is even visible
            // (because internal logic check's if user is logged in and then sets
            // next step automatically to product choose)
            if (get().showProductDialog) {

                // track steps
                const trackedSteps = get().trackedSteps;

                if (step === "choose") {
                    trackStepIfNotTracked("login", trackedSteps);
                    trackedSteps.push("login");
                }

                trackStepIfNotTracked(step, trackedSteps);
                trackedSteps.push(step);

                set({trackedSteps: [...trackedSteps]});
            }
        } ,
        selectedProduct: null,

        addressData: null,
        setAddressData: (addressData) => {
            set({ addressData } )
            get().update();
        },

        hasAddressError: false,
        setHasAddressError: (hasAddressError) => set( { hasAddressError } ),

        errorMessage: "",
        setErrorMessage: (errorMessage) => set( {errorMessage} ),

        paymentMethod: "paypal",
        setPaymentMethod: (paymentMethod) => {
            set( { paymentMethod } );
            trackPosthog("payment product modal change of payment method to " + paymentMethod);
        } ,

        products: [],
        setProducts: (products) => set( { products } ),

        currentProduct: null,
        setCurrentProduct: (product) => {
            set( { currentProduct: product });
            get().update()
        },


        postRequestData: {
            amount: 1,
            id: 1
        },


        trackPayment: (transactionId, paymentMethod) => {

            if (!transactionId) {
                console.log('*** cannot track payment because missing transaction id');
                return null;
            }

            // global variables
            window.transactionId = transactionId;
            window.netto = get().totalPrice / 119.0 * 19;

            trackClearECommerce();
            trackEvent(
                "purchase",
                {
                    ecommerce: {
                        coupon: undefined,
                        payment_type: paymentMethod,
                        items: [
                            {
                                item_id: "PP" + get().currentProduct.id,
                                item_list_id: "C007",
                                item_list_name: "Landingpage Gutscheine",
                                item_name: get().currentProduct.title,
                                item_category: "Geschenk",
                                price: get().totalPrice,
                                item_price: get().currentProduct.price,
                                quantity: 1,
                                currency: 'EUR',
                                affiliation: "Website",
                                index: 0,
                                discount: 0
                            }
                        ],
                        value: get().totalPrice,
                        tax: get().totalPrice / 119.0 * 19
                    }
                }
            );

            trackEvent('ecommercePurchase', {
                'ecommerce': {
                    'payment_type': get().paymentMethod,
                    'purchase': {
                        'actionField': {
                            'id': transactionId,
                            'affiliation': 'Online Store',
                            // Total transaction value (incl. tax and shipping) (somit ergo brutto)
                            'revenue': get().totalPrice,
                            'tax': get().totalPrice / 119.0 * 19,
                            'shipping': '0.00',
                            'action': 'purchase'
                        },
                        'payment_type': get().paymentMethod,
                        'products': [
                            {
                                'name': get().currentProduct.title,
                                'id': "PP" + get().currentProduct.id,
                                'price': get().totalPrice,
                                'brand': '7Hauben',
                                'category': "Geschenk",
                                'variant': '',
                                'quantity': 1,
                                'coupon': ''
                            }
                        ]
                    }
                }
            });

            trackFBEvent('Purchase', {
                content_ids: [ "PP" + get().currentProduct.id ],
                content_name: get().currentProduct.title,
                currency: 'eur',
                num_items: 1,
                contents: [
                    {
                        id: "PP" + get().currentProduct.id,
                        quantity: 1,
                        currency: 'eur',
                        title: get().currentProduct.title
                    }
                ],
                value: get().totalPrice
            });

            // global variables
            window.transactionId = transactionId;
            window.netto = get().totalPrice / 119.0;

        }

    })
);
